import dayjs from 'dayjs';
import dayjsMixin from '../../../../mixins/dayjs';

export default {
  name: 'CalendarDate',
  mixins: [dayjsMixin],
  model: {
    prop: 'value',
    event: 'change',
  },
  events: ['change', 'selected', 'open-month', 'open-year'],
  props: {
    value: {
      type: [String, Object],
      default: null,
    },
    preview: {
      type: [String, Object],
      default: null,
    },
    navigation: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      date: null,
      datePreview: null,
      day: null,
      isSameDate: false,
      week: 7,
      rows: 6,
      weekdaysShort: dayjs.weekdaysShort(),
      transitionEnterClass: '',
      transitionLeaveClass: '',
    };
  },
  computed: {
    totalDays() {
      return this.week * this.rows;
    },
    firstWeekDays() {
      return this.datePreview.startOf('month').day();
    },
    lastWeekDays() {
      return this.totalDays - (this.firstWeekDays + this.daysInMonth);
    },
    prevMonth() {
      return this.datePreview.subtract(1, 'month');
    },
    daysInMonth() {
      return this.datePreview.daysInMonth();
    },
    daysInPrevMonth() {
      return this.prevMonth.daysInMonth();
    },
    daysOptions() {
      const options = [];
      const firstWeekDaysOffset = this.daysInPrevMonth - (this.firstWeekDays - 1);

      for (let firstWeekDay = firstWeekDaysOffset; firstWeekDay <= this.daysInPrevMonth; firstWeekDay++) {
        options.push({
          label: firstWeekDay,
          value: JSON.stringify({
            day: firstWeekDay,
            firstWeekDay: true,
            lastWeekDay: false,
          }),
        });
      }

      for (let day = 1; day <= this.daysInMonth; day++) {
        options.push({
          label: day,
          value: JSON.stringify({
            day,
            firstWeekDay: false,
            lastWeekDay: false,
          }),
        });
      }

      for (let lastWeekDay = 1; lastWeekDay <= this.lastWeekDays; lastWeekDay++) {
        options.push({
          label: lastWeekDay,
          value: JSON.stringify({
            day: lastWeekDay,
            firstWeekDay: false,
            lastWeekDay: true,
          }),
        });
      }

      this.updateCalendarStyle();
      return options;
    },
  },
  watch: {
    value: {
      handler(value) {
        const date = dayjs(value);
        const previewDate = dayjs(value);

        this.date = date.isValid() ? date : dayjs();
        this.datePreview = previewDate.isValid() ? previewDate : this.date;

        this.day = {
          day: this.date.date(),
          firstWeekDay: false,
          lastWeekDay: false,
        };
      },
      immediate: true,
    },
    preview: {
      handler(value) {
        const previewDate = dayjs(value);
        this.datePreview = previewDate.isValid() ? previewDate : this.date;
      },
      immediate: true,
    },
    date(value, oldValue) {
      this.isSameDate =
        value.isSame(oldValue, 'day') &&
        value.isSame(oldValue, 'month') &&
        value.isSame(oldValue, 'year');

      this.emitEvent('change', value);
    },
    datePreview: {
      handler(value, oldValue) {
        const isSamePreview = (value, date) => (
          value.isSame(date, 'month') && value.isSame(date, 'year')
        );

        this.day = {
          day: isSamePreview(value, this.date) ? value.date() : 0,
          firstWeekDay: false,
          lastWeekDay: false,
        };

        if (isSamePreview(value, oldValue)) return;

        if (value.isAfter(oldValue, 'month')) this.updateTransitionClass('next');
        if (value.isBefore(oldValue, 'month')) this.updateTransitionClass('prev');
        if (value.isAfter(oldValue, 'year')) this.updateTransitionClass('next');
        if (value.isBefore(oldValue, 'year')) this.updateTransitionClass('prev');

        this.emitEvent('change-preview', value, false, this.preview);
      },
      immediate: true,
    },
  },
  methods: {
    click() {
      this.$emit('click');
    },
    openMonthView() {
      this.$emit('open-month');
    },
    openYearView() {
      this.$emit('open-year');
    },
    selected() {
      this.emitEvent('selected', this.date, false);
    },
    setDay(value) {
      if (!value) return;
      const dayValue = JSON.parse(value);

      if (dayValue.firstWeekDay) {
        const prevMonth = this.datePreview.subtract(1, 'month');
        this.datePreview = prevMonth.date(dayValue.day);
      } else if (dayValue.lastWeekDay) {
        const nextMonth = this.datePreview.add(1, 'month');
        this.datePreview = nextMonth.date(dayValue.day);
      } else {
        this.datePreview = this.datePreview.date(dayValue.day);
      }

      this.day = JSON.stringify({
        day: this.datePreview.date(),
        firstWeekDay: false,
        lastWeekDay: false,
      });

      this.date = this.datePreview;
      this.selected();
    },
    emitEvent(name, value, checkDate = true, props = this.value) {
      if (typeof props === 'string') {
        this.$emit(name, value.toISOString());
      } else {
        if (checkDate) {
          if (!this.isSameDate) this.$emit(name, value);
        } else {
          this.$emit(name, value);
        }
      }
    },
    async updateCalendarStyle() {
      await this.$nextTick();

      if (this.$refs.days) {
        const firstElm = [...this.$refs.days.$el.children].slice(0, this.firstWeekDays);
        const lastElm = [...this.$refs.days.$el.children].slice(-this.lastWeekDays);
        for (const child of [...firstElm, ...lastElm]) {
          child.classList.add('calendar-date__days--addition-week-days');
        }
      }
    },
    updateTransitionClass(direction) {
      if (direction === 'prev') {
        this.transitionEnterClass = 'animated fadeSlideInLeft';
        this.transitionLeaveClass = 'animated fadeSlideOutRight';
      }
      if (direction === 'next') {
        this.transitionEnterClass = 'animated fadeSlideInRight';
        this.transitionLeaveClass = 'animated fadeSlideOutLeft';
      }
    },
  },
};
